/**
 * Flash Sale Drawer (standalone — no theme dependencies).
 * Uses native <dialog> showModal() / close() with custom slide-in animation.
 */

class FlashSaleDrawerComponent extends HTMLElement {
  #dialog = null;
  #abort = null;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  open() {
    if (!this.#dialog || this.#dialog.open) return;
    this.#dialog.showModal();
    this.#dialog.classList.remove('flash-sale-drawer__dialog--closing');
    document.documentElement.style.overflow = 'hidden';

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    // Backdrop click closes
    this.#dialog.addEventListener('click', (e) => {
      const rect = this.#dialog.getBoundingClientRect();
      const clickedInside =
        e.clientX >= rect.left && e.clientX <= rect.right &&
        e.clientY >= rect.top && e.clientY <= rect.bottom;
      if (!clickedInside) this.close();
    }, { signal });

    // Escape (native dialog cancel) — animate close instead of instant
    this.#dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.close();
    }, { signal });
  }

  close() {
    if (!this.#dialog || !this.#dialog.open) return;
    this.#abort?.abort();
    this.#dialog.classList.add('flash-sale-drawer__dialog--closing');
    const onEnd = () => {
      this.#dialog.removeEventListener('animationend', onEnd);
      this.#dialog.close();
      this.#dialog.classList.remove('flash-sale-drawer__dialog--closing');
      document.documentElement.style.overflow = '';
    };
    this.#dialog.addEventListener('animationend', onEnd);
    // Fallback in case animationend doesn't fire (reduced-motion etc.)
    setTimeout(onEnd, 380);
  }
}

if (!customElements.get('flash-sale-drawer-component')) {
  customElements.define('flash-sale-drawer-component', FlashSaleDrawerComponent);
}

/**
 * Countdown timer rendered as a single dark pill (e.g. "7J 23H 59M 55S").
 */
class FlashSaleCountdown extends HTMLElement {
  #intervalId = null;

  connectedCallback() {
    this.#render();
    this.#intervalId = setInterval(() => this.#render(), 1000);
  }

  disconnectedCallback() {
    if (this.#intervalId) clearInterval(this.#intervalId);
  }

  #stop() {
    if (this.#intervalId) {
      clearInterval(this.#intervalId);
      this.#intervalId = null;
    }
  }

  #render() {
    const endStr = this.getAttribute('data-end');
    if (!endStr) { this.style.display = 'none'; return; }
    const end = new Date(endStr).getTime();
    if (Number.isNaN(end)) { this.style.display = 'none'; return; }

    const diff = end - Date.now();
    if (diff <= 0) {
      const action = this.dataset.zeroAction || 'leave';
      if (action === 'hide') {
        const offer = this.closest('.flash-sale-offer');
        if (offer) offer.style.display = 'none';
        else this.style.display = 'none';
      } else {
        this.#renderDigits(0, 0, 0, 0);
      }
      this.#stop();
      return;
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((diff / (1000 * 60)) % 60);
    const seconds = Math.floor((diff / 1000) % 60);
    this.#renderDigits(days, hours, minutes, seconds);
  }

  #renderDigits(days, hours, minutes, seconds) {
    const showLabels = (this.dataset.showLabels || 'true') !== 'false';
    const labels = (this.dataset.labels || 'J,H,M,S').split(',');
    const parts = [
      [days, labels[0]],
      [hours, labels[1]],
      [minutes, labels[2]],
      [seconds, labels[3]],
    ];

    this.innerHTML = parts
      .map(([value, label]) => {
        const num = String(value).padStart(2, '0');
        const labelHtml = showLabels && label
          ? `<span class="flash-sale-countdown__label">${label}</span>`
          : '';
        return `<span class="flash-sale-countdown__group"><span class="flash-sale-countdown__digit">${num}</span>${labelHtml}</span>`;
      })
      .join('');
  }
}

if (!customElements.get('flash-sale-countdown')) {
  customElements.define('flash-sale-countdown', FlashSaleCountdown);
}

/**
 * Global click delegation:
 *  - [data-flash-sale-trigger] opens the drawer
 *  - [data-flash-sale-copy]    copies code to clipboard
 */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest && event.target.closest('[data-flash-sale-trigger]');
  if (trigger) {
    event.preventDefault();
    const drawer = document.querySelector('flash-sale-drawer-component');
    if (drawer) drawer.open();
    return;
  }

  const copyBtn = event.target.closest && event.target.closest('[data-flash-sale-copy]');
  if (copyBtn) {
    event.preventDefault();
    event.stopPropagation();
    const code = copyBtn.getAttribute('data-code');
    if (!code || !navigator.clipboard) return;
    navigator.clipboard.writeText(code).then(() => {
      copyBtn.classList.add('flash-sale-offer__code--copied');
      setTimeout(() => {
        copyBtn.classList.remove('flash-sale-offer__code--copied');
      }, 1500);
    });
  }
});
